<template>
  <v-dialog
    :value="show"
    max-width="1200"
    persistent
    scrollable
    :fullscreen="$vuetify.breakpoint.xsOnly"
    @input="$emit('close')"
  >
    <v-card>
      <v-card-title>
        <span class="headline">Select Specialist</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-card-text style="max-height: calc(100vh - 300px); overflow-y: auto;">
        <v-row>
          <!-- Filters -->
          <v-col cols="12" md="4">
            <h3 class="subtitle-1 mb-3">Filters</h3>
            
            <!-- Specialty (read-only, set by parent) -->
            <v-text-field
              :value="specialty"
              label="Specialty"
              outlined
              dense
              readonly
              class="mb-3"
            ></v-text-field>
            
            <!-- Search by Name -->
            <v-text-field
              v-model="searchTerm"
              label="Search for Name"
              outlined
              clearable
              dense
              class="mb-3"
              @input="debouncedSearch"
              @keyup.enter="searchProviders"
            ></v-text-field>
            
            <!-- Search by Zipcode -->
            <v-text-field
              v-model="zipcode"
              label="Zipcode"
              outlined
              clearable
              dense
              class="mb-3"
              placeholder="ZIP, e.g. 33142 (radius from ZIP / location center)"
              @input="debouncedSearch"
              @keyup.enter="searchProviders"
            ></v-text-field>
            
            <!-- Results Count -->
            <v-card class="mt-4" outlined>
              <v-card-text>
                <div class="text-h6">{{ providers.length }} providers found</div>
              </v-card-text>
            </v-card>
            
            <!-- Provider List -->
            <v-list class="mt-4" max-height="400" style="overflow-y: auto;">
              <v-list-item
                v-for="provider in providers"
                :key="providerRowKey(provider)"
                @click="selectProvider(provider)"
                :class="{ 'primary lighten-4': selectedProvider && isSameProviderRow(selectedProvider, provider) }"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ provider.PROVIDER_NAME }}</v-list-item-title>
                  <v-list-item-subtitle class="text-wrap">
                    <div v-if="provider.SERVICE_LOCATION_ADDRESS_1">{{ provider.SERVICE_LOCATION_ADDRESS_1 }}</div>
                    <div v-if="provider.SERVICE_LOCATION_ADDRESS_2">{{ provider.SERVICE_LOCATION_ADDRESS_2 }}</div>
                    <div v-if="provider.SERVICE_LOCATION_CITY || provider.SERVICE_LOCATION_STATE">
                      {{ provider.SERVICE_LOCATION_CITY
                      }}<template v-if="provider.SERVICE_LOCATION_STATE"
                        >, {{ provider.SERVICE_LOCATION_STATE }}</template
                      >
                    </div>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-col>
          
          <!-- Map -->
          <v-col cols="12" md="8">
            <div :id="mapId" style="width: 100%; height: 500px;"></div>
          </v-col>
        </v-row>
      </v-card-text>
      
      <!-- Selected Provider Details -->
      <v-card-actions v-if="selectedProvider" class="px-4 pt-0 pb-2">
        <v-card class="flex-grow-1" outlined>
          <v-card-title class="primary white--text">
            <v-icon class="mr-2" color="white">mdi-doctor</v-icon>
            <span class="font-weight-bold">Provider Details</span>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="selectedProvider = null; hasSelectedProvider = false;">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          
          <v-card-text class="pa-6">
            <div v-if="enrollmentMemberInfoRows.length" class="mb-4">
              <div class="text-subtitle-2 mb-2">Member information</div>
              <div
                v-for="(row, i) in enrollmentMemberInfoRows"
                :key="'sp-mem-' + i"
                class="mb-3"
              >
                <div class="d-flex align-center mb-1">
                  <v-icon class="mr-2 grey--text" small>mdi-account-outline</v-icon>
                  <span class="caption grey--text text--darken-1 font-weight-medium">{{ row.label }}</span>
                </div>
                <div class="body-1 ml-8">{{ row.value }}</div>
              </div>
            </div>
            <v-divider v-if="enrollmentMemberInfoRows.length" class="my-4"></v-divider>
            <!-- Provider Name and Specialty -->
            <div class="mb-4">
              <div class="text-h5 font-weight-bold mb-1">
                {{ selectedProvider.PROVIDER_NAME }}
              </div>
              <v-chip color="primary" outlined class="mt-1">
                <v-icon small left>mdi-stethoscope</v-icon>
                {{ specialty }}
              </v-chip>
            </div>
            
            <v-divider class="my-4"></v-divider>
            
            <!-- Provider ID -->
            <div v-if="selectedProvider.PROVIDER_ID" class="mb-4">
              <div class="d-flex align-center mb-2">
                <v-icon class="mr-2 grey--text">mdi-identifier</v-icon>
                <span class="caption grey--text text--darken-1 font-weight-medium">Provider ID</span>
              </div>
              <div class="body-1 ml-8">{{ selectedProvider.PROVIDER_ID }}</div>
            </div>

            <div class="mb-4">
              <div class="d-flex align-center mb-2">
                <v-icon class="mr-2 grey--text">mdi-human-male-female</v-icon>
                <span class="caption grey--text text--darken-1 font-weight-medium">Gender</span>
              </div>
              <div class="body-1 ml-8">{{ selectedProviderGenderLine }}</div>
            </div>

            <div class="mb-4">
              <div class="d-flex align-center mb-2">
                <v-icon class="mr-2 grey--text">mdi-certificate</v-icon>
                <span class="caption grey--text text--darken-1 font-weight-medium">Board Certified</span>
              </div>
              <div class="body-1 ml-8">{{ selectedProviderBoardCertifiedLine }}</div>
            </div>
            
            <!-- Address -->
            <div v-if="selectedProvider.SERVICE_LOCATION_ADDRESS_1 || selectedProvider.SERVICE_LOCATION_CITY" class="mb-4">
              <div class="d-flex align-center mb-2">
                <v-icon class="mr-2 grey--text">mdi-map-marker</v-icon>
                <span class="caption grey--text text--darken-1 font-weight-medium">Address</span>
              </div>
              <div class="body-1 ml-8">
                <div v-if="selectedProvider.SERVICE_LOCATION_ADDRESS_1">
                  {{ selectedProvider.SERVICE_LOCATION_ADDRESS_1 }}
                </div>
                <div v-if="selectedProvider.SERVICE_LOCATION_ADDRESS_2" class="mt-1">
                  {{ selectedProvider.SERVICE_LOCATION_ADDRESS_2 }}
                </div>
                <div v-if="selectedProvider.SERVICE_LOCATION_CITY" class="mt-1">
                  {{ selectedProvider.SERVICE_LOCATION_CITY }}{{ selectedProvider.SERVICE_LOCATION_STATE ? ', ' + selectedProvider.SERVICE_LOCATION_STATE : '' }}
                  {{ selectedProvider.SERVICE_LOCATION_ZIP || '' }}
                </div>
              </div>
            </div>
            
            <!-- Phone Numbers -->
            <div v-if="selectedProvider.BILLING_PHONE || selectedProvider.SERVICE_LOCATION_PHONE" class="mb-4">
              <div class="d-flex align-center mb-2">
                <v-icon class="mr-2 grey--text">mdi-phone</v-icon>
                <span class="caption grey--text text--darken-1 font-weight-medium">Phone</span>
              </div>
              <div class="body-1 ml-8">
                <div v-if="selectedProvider.BILLING_PHONE" class="mb-1">
                  <span class="caption grey--text mr-2">Billing:</span>
                  <a :href="`tel:${selectedProvider.BILLING_PHONE}`" class="text-decoration-none">
                    {{ selectedProvider.BILLING_PHONE }}
                  </a>
                </div>
                <div v-if="selectedProvider.SERVICE_LOCATION_PHONE">
                  <span class="caption grey--text mr-2">Service Location:</span>
                  <a :href="`tel:${selectedProvider.SERVICE_LOCATION_PHONE}`" class="text-decoration-none">
                    {{ selectedProvider.SERVICE_LOCATION_PHONE }}
                  </a>
                </div>
              </div>
            </div>
            
            <!-- Languages -->
            <div v-if="selectedProvider.LANGUAGE || selectedProvider.LANGUAGES" class="mb-4">
              <div class="d-flex align-center mb-2">
                <v-icon class="mr-2 grey--text">mdi-translate</v-icon>
                <span class="caption grey--text text--darken-1 font-weight-medium">Languages</span>
              </div>
              <div class="ml-8">
                <div v-if="selectedProvider.LANGUAGES && Array.isArray(selectedProvider.LANGUAGES) && selectedProvider.LANGUAGES.length > 0">
                  <v-chip
                    v-for="(lang, index) in selectedProvider.LANGUAGES"
                    :key="index"
                    small
                    class="mr-2 mb-1"
                    color="primary"
                    outlined
                  >
                    {{ lang }}
                  </v-chip>
                </div>
                <div v-else-if="selectedProvider.LANGUAGE" class="body-1">
                  <v-chip
                    small
                    color="primary"
                    outlined
                  >
                    {{ selectedProvider.LANGUAGE }}
                  </v-chip>
                </div>
              </div>
            </div>
            
            <!-- Primary Specialty -->
            <div v-if="selectedProvider.PRIMARY_SPECIALTY" class="mb-4">
              <div class="d-flex align-center mb-2">
                <v-icon class="mr-2 grey--text">mdi-hospital-box</v-icon>
                <span class="caption grey--text text--darken-1 font-weight-medium">Primary Specialty</span>
              </div>
              <div class="body-1 ml-8">{{ selectedProvider.PRIMARY_SPECIALTY }}</div>
            </div>
            
            <!-- Coordinates (if available) -->
            <div v-if="selectedProvider.SERVICE_LOCATION_LAT && selectedProvider.SERVICE_LOCATION_LON" class="mb-4">
              <div class="d-flex align-center mb-2">
                <v-icon class="mr-2 grey--text">mdi-crosshairs-gps</v-icon>
                <span class="caption grey--text text--darken-1 font-weight-medium">Location</span>
              </div>
              <div class="body-2 ml-8 grey--text">
                Coordinates: {{ selectedProvider.SERVICE_LOCATION_LAT }}, {{ selectedProvider.SERVICE_LOCATION_LON }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-card-actions>
      
      <!-- OPTIMIZATION: Fixed actions bar that stays visible at bottom -->
      <v-card-actions class="sticky-actions">
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('close')">Cancel</v-btn>
        <v-btn
          color="primary"
          :disabled="!hasSelectedProvider"
          @click="confirmSelection"
        >
          Select Specialist
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { boardCertifiedDisplay, buildMemberInfoRows, providerGenderLine } from '../../utils/fhcProviderMemberDisplay';

// Debounce helper
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default {
  name: 'SpecialistSelectionModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    specialty: {
      type: String,
      required: false,
      default: null
    },
    member: {
      type: Object,
      default: null
    },
    insurance: {
      type: String,
      required: true
    }
  },
  data() {
      // Generate unique map ID for each instance using component instance ID
      // Use a static counter to ensure uniqueness across component instances
      if (!window.__mapInstanceCounter) {
        window.__mapInstanceCounter = 0;
      }
      window.__mapInstanceCounter++;
      const mapId = `specialist-selection-map-${window.__mapInstanceCounter}`;
      
      const data = {
        mapboxAccessToken: 'pk.eyJ1IjoiamJmb2xpdmVpcmEiLCJhIjoiY21naDJxd3lqMGRucjJtcHk0OXBwYmVpMCJ9.vZnIqcYZjZj05ZARV8jd8w',
        providers: [],
        searchTerm: '',
        zipcode: '',
        zipSearchRadiusMiles: 15,
        providersWithCoordsCount: 0,
        subscriberMarker: null,
        subscriberCoords: null,
        mapId: mapId // Unique ID for this map instance
      };
    
    // CRITICAL FIX: Store Mapbox-related objects outside Vue reactivity to prevent DevTools crashes
    // Mapbox objects are extremely deeply nested and cause infinite loops when DevTools inspects them
    let mapInstance = null;
    let markersInstance = [];
    let popupsInstance = [];
    let selectedProviderInstance = null;
    
    Object.defineProperty(data, 'map', {
      get() { return mapInstance; },
      set(value) { mapInstance = value; },
      enumerable: false,
      configurable: true
    });
    
    Object.defineProperty(data, 'markers', {
      get() { return markersInstance; },
      set(value) { markersInstance = Array.isArray(value) ? value : []; },
      enumerable: false,
      configurable: true
    });
    
    Object.defineProperty(data, 'popups', {
      get() { return popupsInstance; },
      set(value) { popupsInstance = Array.isArray(value) ? value : []; },
      enumerable: false,
      configurable: true
    });
    
    // selectedProvider needs to be a normal reactive property for Vue to track it
    // We'll use a simple flag for button state to avoid reactivity issues
    data.selectedProvider = null;
    data.hasSelectedProvider = false;
    
    return data;
  },
  computed: {
    enrollmentMemberInfoRows() {
      return buildMemberInfoRows(this.member);
    },
    selectedProviderBoardCertifiedLine() {
      return boardCertifiedDisplay(this.selectedProvider) || '—';
    },
    selectedProviderGenderLine() {
      return providerGenderLine(this.selectedProvider);
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        // Wait for DOM to be ready
        this.$nextTick(() => {
          // Wait a bit more to ensure container is in DOM
          setTimeout(() => {
            if (this.show && !this.map) {
              // Verify container exists before initializing
              const container = document.getElementById(this.mapId);
              if (container) {
                console.log('✅ Container found, initializing map...');
                this.initMap();
                
                // Wait for map to be ready before searching
                const checkMapReady = () => {
                  if (this.map && this.map.isStyleLoaded()) {
                    if (this.show && this.specialty && this.specialty.trim()) {
                      this.searchProviders();
                    }
                  } else if (this.map) {
                    // Map exists but not ready, wait for load event
                    this.map.once('load', () => {
                      if (this.show && this.specialty && this.specialty.trim()) {
                        this.searchProviders();
                      }
                    });
                    // Timeout fallback
                    setTimeout(() => {
                      if (this.show && this.map && this.map.isStyleLoaded() && this.specialty && this.specialty.trim()) {
                        this.searchProviders();
                      }
                    }, 2000);
                  } else {
                    // Map not created yet, try again
                    setTimeout(checkMapReady, 100);
                  }
                };
                
                // Start checking after a short delay
                setTimeout(checkMapReady, 500);
              } else {
                console.warn('⚠️ Container not found, retrying...');
                setTimeout(() => {
                  if (this.show && !this.map) {
                    this.initMap();
                  }
                }, 200);
              }
            }
          }, 200);
        });
      } else {
        this.cleanupMap();
        // Reset when closing
        this.selectedProvider = null;
        this.hasSelectedProvider = false;
      }
    },
    specialty: {
      handler(newVal, oldVal) {
        if (this.show && newVal && newVal.trim()) {
          // Reset search and providers when specialty changes
          this.providers = [];
          this.searchTerm = '';
          this.searchProviders();
        }
      },
      immediate: true
    }
  },
  mounted() {
    if (this.show) {
      this.$nextTick(() => {
        if (!this.map) {
          this.initMap();
        }
        // Only search if specialty is available
        if (this.specialty && this.specialty.trim()) {
          this.searchProviders();
        }
      });
    }
  },
  beforeDestroy() {
    this.cleanupMap();
  },
  methods: {
    providerRowKey(provider) {
      if (!provider) return '';
      const pract =
        provider.PRACTICE_ID !== undefined &&
        provider.PRACTICE_ID !== null &&
        String(provider.PRACTICE_ID).trim() !== ''
          ? String(provider.PRACTICE_ID)
          : '';
      return `${provider.PROVIDER_ID}::${pract}`;
    },
    isSameProviderRow(a, b) {
      if (!a || !b) return false;
      const pa =
        a.PRACTICE_ID !== undefined && a.PRACTICE_ID !== null && String(a.PRACTICE_ID).trim() !== ''
          ? String(a.PRACTICE_ID)
          : '';
      const pb =
        b.PRACTICE_ID !== undefined && b.PRACTICE_ID !== null && String(b.PRACTICE_ID).trim() !== ''
          ? String(b.PRACTICE_ID)
          : '';
      return String(a.PROVIDER_ID) === String(b.PROVIDER_ID) && pa === pb;
    },
    initMap() {
      // Always cleanup first to ensure clean state
      if (this.map) {
        console.log('🧹 Cleaning up existing map before creating new one...');
        this.cleanupMap();
        // Wait a bit after cleanup
        setTimeout(() => {
          this.createMapInstance();
        }, 300);
      } else {
        this.createMapInstance();
      }
    },
    createMapInstance() {
      const mapContainer = document.getElementById(this.mapId);
      if (!mapContainer) {
        console.warn('⚠️ Map container not found:', this.mapId);
        setTimeout(() => { 
          if (this.show && !this.map) {
            this.createMapInstance();
          }
        }, 100);
        return;
      }
      
      // Check if container is visible
      const computedStyle = window.getComputedStyle(mapContainer);
      console.log('📋 Container styles:', {
        display: computedStyle.display,
        visibility: computedStyle.visibility,
        width: computedStyle.width,
        height: computedStyle.height,
        position: computedStyle.position
      });
      
      if (computedStyle.display === 'none') {
        mapContainer.style.display = 'block';
      }
      if (computedStyle.visibility === 'hidden') {
        mapContainer.style.visibility = 'visible';
      }
      
      // Ensure container is empty
      mapContainer.innerHTML = '';
      
      // Verify container has dimensions - CRITICAL for Mapbox
      const rect = mapContainer.getBoundingClientRect();
      console.log('📐 Container dimensions:', {
        width: rect.width,
        height: rect.height,
        top: rect.top,
        left: rect.left
      });
      
      if (rect.width === 0 || rect.height === 0) {
        console.warn('⚠️ Map container has no dimensions, waiting...');
        setTimeout(() => {
          if (this.show && !this.map) {
            this.createMapInstance();
          }
        }, 200);
        return;
      }
      
      // CRITICAL: Wait a bit more to ensure container is fully in DOM and visible
      setTimeout(() => {
        if (!this.show || this.map) {
          return; // Modal closed or map already created
        }
        
        const finalContainer = document.getElementById(this.mapId);
        if (!finalContainer) {
          console.error('❌ Container disappeared!');
          return;
        }
        
        const finalRect = finalContainer.getBoundingClientRect();
        if (finalRect.width === 0 || finalRect.height === 0) {
          console.error('❌ Container still has no dimensions!');
          return;
        }
        
        console.log('🗺️ Creating new map instance in container:', this.mapId);
        
        try {
          mapboxgl.accessToken = this.mapboxAccessToken;
          
          // Create map with explicit dimensions
          this.map = new mapboxgl.Map({
            container: this.mapId,
            style: 'mapbox://styles/mapbox/streets-v11',
            center: [-80.1918, 25.7617],
            zoom: 10,
            preserveDrawingBuffer: true,
            antialias: true,
            fadeDuration: 0 // Disable fade to see if it helps
          });
          
          this.map.addControl(new mapboxgl.NavigationControl());
          
          // CRITICAL: Wait for style to load before considering map ready
          this.map.once('load', () => {
            console.log('✅ Map style loaded successfully for:', this.mapId);
            
            // Force resize multiple times to ensure rendering
            this.$nextTick(() => {
              if (this.map) {
                this.map.resize();
                setTimeout(() => {
                  if (this.map) {
                    this.map.resize();
                    console.log('✅ Map resized (second time)');
                  }
                }, 100);
              }
            });
          });
          
          // Listen for style loading errors
          this.map.once('error', (e) => {
            console.error('❌ Map error for', this.mapId, ':', e);
            console.error('Error details:', JSON.stringify(e, null, 2));
          });
          
          // Listen for style data loading
          this.map.on('styledata', () => {
            console.log('📋 Map style data loaded for:', this.mapId);
          });
          
          // Listen for render events
          let renderCount = 0;
          this.map.on('render', () => {
            renderCount++;
            if (renderCount <= 5) {
              console.log(`🖼️ Map rendering (${renderCount}) for:`, this.mapId);
            }
          });
          
          // Also listen for data events
          this.map.on('data', (e) => {
            if (e.dataType === 'style') {
              console.log('📋 Style data event for:', this.mapId);
            }
          });
          
          console.log('✅ Map instance created successfully');
        } catch (error) {
          console.error('❌ Error creating map instance:', error);
          console.error('Error stack:', error.stack);
          this.map = null;
        }
      }, 300); // Wait 300ms to ensure container is ready
    },
    cleanupMap() {
      console.log('🧹 Cleaning up map for:', this.mapId);
      
      // Clear markers first
      this.clearMarkers();
      
      // Remove map instance
      if (this.map) {
        try {
          // Remove all event listeners
          this.map.off();
          // Remove the map
          this.map.remove();
          console.log('✅ Map removed successfully');
        } catch (e) {
          console.error('❌ Error removing map:', e);
        }
        this.map = null;
      }
      
      // Clear the container HTML to ensure clean state
      const mapContainer = document.getElementById(this.mapId);
      if (mapContainer) {
        mapContainer.innerHTML = '';
        console.log('✅ Container cleared');
      } else {
        console.warn('⚠️ Container not found for cleanup:', this.mapId);
      }
    },
    debouncedSearch: debounce(function() {
      this.searchProviders();
    }, 500),
    async searchProviders() {
      try {
        // Validate specialty before searching
        if (!this.specialty || !this.specialty.trim()) {
          this.providers = [];
          return;
        }
        
        // Build query parameters
        let queryParams = [];
        
        queryParams.push(`specialty=${encodeURIComponent(this.specialty.trim())}`);
        
        // Add search parameter if there's a search term
        if (this.searchTerm && this.searchTerm.trim()) {
          const searchValue = this.searchTerm.trim();
          queryParams.push(`search=${encodeURIComponent(searchValue)}`);
        }
        
        // Add zipcode parameter if there's a zipcode
        if (this.zipcode && this.zipcode.trim()) {
          const zipcodeValue = this.zipcode.trim();
          queryParams.push(`location=${encodeURIComponent(zipcodeValue)}`);
          queryParams.push(`radiusMiles=${encodeURIComponent(String(this.zipSearchRadiusMiles || 15))}`);
        }
        
        const queryString = queryParams.join('&');
        // Add cache busting timestamp
        const timestamp = new Date().getTime();
        const url = `/api/providers/mapsearch/all${queryString ? '?' + queryString + '&' : '?'}t=${timestamp}`;
        
        const response = await axios.get(url, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          },
          validateStatus: function (status) {
            // Accept both 200 and 304
            return status >= 200 && status < 300 || status === 304;
          }
        });
        
        // Handle 304 Not Modified - if status is 304, data might be empty but we should use cached data
        // However, since we're using cache busting, 304 shouldn't happen. But if it does, treat as empty
        let providers = [];
        if (response.status === 304) {

          providers = [];
        } else {
          providers = Array.isArray(response.data) ? response.data : (response.data ? [response.data] : []);
        }
        
        // Debug log - always log to see what's happening

        if (providers.length > 0) {

        } else {

        }
        
        // Force update - ensure Vue reactivity by creating new array reference
        // Create a new array and sort alphabetically by PROVIDER_NAME
        let newProviders = Array.isArray(providers) ? [...providers] : [];
        // Sort alphabetically by PROVIDER_NAME (case-insensitive)
        newProviders.sort((a, b) => {
          const nameA = (a.PROVIDER_NAME || '').toLowerCase();
          const nameB = (b.PROVIDER_NAME || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        this.$set(this, 'providers', newProviders);
        this.providersWithCoordsCount = 0; // Reset before updateMapMarkers calculates it
        
        // Log to verify Vue is tracking the change

        // Wait for DOM update and then update map markers
        await this.$nextTick();

        // Validate providers before calling updateMapMarkers
        if (this.providers && Array.isArray(this.providers)) {
          this.updateMapMarkers();
        } else {

        }
      } catch (error) {
        this.providers = [];
        if (error.response) {

        } else {

        }
      }
    },
    async updateMapMarkers() {
      // Store providers in a local variable to avoid issues if it gets reset
      const providersList = this.providers;
      
      // Validate inputs BEFORE clearing markers
      if (!this.map) {
        console.warn('⚠️ Map not initialized, cannot add markers');
        return;
      }
      
      // CRITICAL: Wait for map to be fully loaded before adding markers
      // The 'idle' event is fired when the map has finished rendering and is ready for interaction
      if (!this.map.loaded()) {
        console.log('⏳ Map not fully loaded, waiting for idle event...');
        // Wait for map to be idle (fully rendered)
        await new Promise((resolve) => {
          // Check if already loaded
          if (this.map && this.map.loaded()) {
            console.log('✅ Map already loaded');
            resolve();
            return;
          }
          
          let resolved = false;
          
          // Listen for 'idle' event - this means map is fully rendered and ready
          const onIdle = () => {
            if (!resolved && this.map) {
              resolved = true;
              console.log('✅ Map idle event received - map is ready for markers');
              if (this.map) {
                this.map.off('idle', onIdle);
                this.map.off('load', onLoad);
              }
              resolve();
            }
          };
          
          // Also listen for 'load' as fallback
          const onLoad = () => {
            if (!resolved && this.map) {
              // Wait a bit more after load event to ensure map is truly ready
              setTimeout(() => {
                if (!resolved && this.map) {
                  resolved = true;
                  console.log('✅ Map load event received - proceeding with markers');
                  if (this.map) {
                    this.map.off('idle', onIdle);
                    this.map.off('load', onLoad);
                  }
                  resolve();
                }
              }, 200);
            }
          };
          
          // Listen for both events
          if (this.map) {
            this.map.once('idle', onIdle);
            this.map.once('load', onLoad);
            
            // Timeout after 10 seconds to prevent infinite wait
            setTimeout(() => {
              if (!resolved) {
                resolved = true;
                console.warn('⚠️ Timeout waiting for map to load, proceeding anyway');
                if (this.map) {
                  this.map.off('idle', onIdle);
                  this.map.off('load', onLoad);
                }
                resolve();
              }
            }, 10000);
          } else {
            resolve(); // Map was removed, exit
          }
        });
      }
      
      // Final check - ensure map is still valid
      if (!this.map) {
        console.warn('⚠️ Map was removed, cannot add markers');
        return;
      }
      
      console.log('✅ Map confirmed ready, proceeding to add markers');
      
      if (!providersList || !Array.isArray(providersList)) {

        return;
      }
      
      if (providersList.length === 0) {

        return;
      }

      // Clear markers AFTER validation - ensure markers exists
      if (this.markers && Array.isArray(this.markers)) {
        this.clearMarkers();
      } else {
        // Initialize markers if it doesn't exist
        this.markers = [];

      }
      
      // Double-check providers after clearMarkers (just in case)
      if (!this.providers || !Array.isArray(this.providers)) {

        // Use the stored list instead
        if (!providersList || !Array.isArray(providersList)) {
          return;
        }
      }
      
      let hasValidCoords = 0;
      const bounds = new mapboxgl.LngLatBounds();
      
      // TEMPORARILY DISABLED: Add subscriber location marker if member has address
      // This is being tested to see if it causes the black map issue
      // if (this.member && this.member.MEMBER_ID) {
      //   try {
      //     const memberResponse = await axios.get(`/api/enrollment/member/${this.insurance}/${this.member.MEMBER_ID}`);
      //     if (memberResponse.data && memberResponse.data.member) {
      //       // Merge member data with address information
      //       this.member = {
      //         ...this.member,
      //         ...memberResponse.data.member
      //       };
      //     }
      //   } catch (error) {
      //     // Continue with existing member data if fetch fails
      //   }
      // }
      // await this.addSubscriberMarker(bounds);
      
      // Use stored providersList instead of this.providers to avoid reactivity issues
      let skippedCount = 0;
      providersList.forEach((provider, index) => {
        // Convert to numbers if they're strings
        const latRaw = provider.SERVICE_LOCATION_LAT;
        const lonRaw = provider.SERVICE_LOCATION_LON;
        const lat = typeof latRaw === 'string' 
          ? parseFloat(latRaw) 
          : latRaw;
        const lon = typeof lonRaw === 'string' 
          ? parseFloat(lonRaw) 
          : lonRaw;
        
        // Log first few providers for debugging
        if (index < 3) {

        }
        
        // Validate coordinates (allow both string and number, but convert to number)
        if (lat != null && lon != null && 
            !isNaN(lat) && !isNaN(lon) &&
            typeof lat === 'number' && typeof lon === 'number' &&
            lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
          
          hasValidCoords++;
          const coordinates = [lon, lat];
          
          // Create marker with custom pin icon
          const el = document.createElement('div');
          el.className = 'marker';
          el.style.width = '40px';
          el.style.height = '40px';
          el.style.cursor = 'pointer';
          
          // Create SVG pin icon
          const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          svg.setAttribute('width', '40');
          svg.setAttribute('height', '40');
          svg.setAttribute('viewBox', '0 0 24 24');
          svg.style.display = 'block';
          
          // Pin shape (path)
          const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          path.setAttribute('d', 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z');
          path.setAttribute('fill', '#1976d2');
          path.setAttribute('stroke', '#ffffff');
          path.setAttribute('stroke-width', '1.5');
          
          svg.appendChild(path);
          el.appendChild(svg);
          
          // Add shadow effect
          el.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))';
          
          try {
            // CRITICAL: Only add marker if map exists
            if (!this.map) {
              console.warn('⚠️ Map not available, skipping marker for provider:', provider.PROVIDER_ID);
              skippedCount++;
              return;
            }
            
            // CRITICAL: Ensure map container is visible before adding marker
            const mapContainer = document.getElementById(this.mapId);
            if (mapContainer) {
              const computedStyle = window.getComputedStyle(mapContainer);
              if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
                console.warn('⚠️ Map container is hidden, cannot add marker');
                skippedCount++;
                return;
              }
            }
            
            const marker = new mapboxgl.Marker(el)
              .setLngLat(coordinates)
              .addTo(this.map);
            
            // Verify marker was added
            const markerElement = marker.getElement();
            if (!markerElement) {
              console.error(`❌ Marker element not created for ${provider.PROVIDER_NAME}`);
              skippedCount++;
              return;
            }
            
            // Add click handler
            markerElement.addEventListener('click', () => {
              this.selectProvider(provider);
            });
            
            this.markers.push(marker);
            bounds.extend(coordinates);
            
            if (index < 3) {
              console.log(`✅ Added marker ${index + 1} for ${provider.PROVIDER_NAME} at ${lat}, ${lon}`);
              console.log(`   Marker element:`, markerElement);
              console.log(`   Marker visible:`, markerElement.offsetParent !== null);
            }
          } catch (error) {

            skippedCount++;
          }
        } else {
          skippedCount++;
          if (index < 3) {

          }
        }
      });

      this.providersWithCoordsCount = hasValidCoords;

      // CRITICAL: Force map resize and re-render after adding markers
      // This ensures the map renders correctly after markers are added
      if (this.map) {
        try {
          // Small delay to ensure DOM is updated
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Check if map container is visible
          const mapContainer = document.getElementById(this.mapId);
          if (mapContainer) {
            const computedStyle = window.getComputedStyle(mapContainer);
            console.log('📋 Map container visibility:', {
              display: computedStyle.display,
              visibility: computedStyle.visibility,
              width: computedStyle.width,
              height: computedStyle.height,
              zIndex: computedStyle.zIndex
            });
            
            // Ensure container is visible
            if (computedStyle.display === 'none') {
              mapContainer.style.display = 'block';
            }
            if (computedStyle.visibility === 'hidden') {
              mapContainer.style.visibility = 'visible';
            }
          }
          
          // Force resize - this is critical for Mapbox to render correctly
          this.map.resize();
          
          // DO NOT move the map - any movement causes it to disappear
          // Just resize is enough to ensure proper rendering
          
          // Also check if style is loaded
          if (!this.map.isStyleLoaded()) {
            console.warn('⚠️ Map style not loaded after adding markers, waiting...');
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } catch (resizeError) {
          console.error('❌ Error resizing/re-rendering map:', resizeError);
        }
      }

      // CRITICAL: DO NOT call fitBounds or flyTo after adding markers
      // This causes the map to disappear. The map will stay at its current position
      // and the user can manually navigate to see the markers.
      // If you need to adjust the view, do it only when the user explicitly requests it.
      
      console.log(`✅ Added ${this.markers.length} markers to map.`);
      console.log(`📊 Markers summary: ${this.markers.length} total, ${hasValidCoords} with valid coordinates, ${skippedCount} skipped`);
      
      // Verify markers are actually in the DOM
      if (this.markers.length > 0) {
        this.markers.forEach((marker, idx) => {
          const element = marker.getElement();
          if (element) {
            const isVisible = element.offsetParent !== null;
            const styles = window.getComputedStyle(element);
            console.log(`📍 Marker ${idx + 1}: visible=${isVisible}, display=${styles.display}, visibility=${styles.visibility}, zIndex=${styles.zIndex}`);
          }
        });
      }
      
      // Adjust map view to show all markers
      if (this.map && this.markers.length > 0 && !bounds.isEmpty()) {
        try {
          // Wait a bit for markers to be fully rendered
          setTimeout(() => {
            if (this.map && this.markers.length > 0) {
              // Use fitBounds to show all markers with padding
              this.map.fitBounds(bounds, {
                padding: { top: 50, bottom: 50, left: 50, right: 50 },
                maxZoom: 15, // Don't zoom in too much
                duration: 1000 // Smooth animation
              });
              console.log('✅ Map adjusted to show all markers');
            }
          }, 300);
        } catch (error) {
          console.error('❌ Error adjusting map bounds:', error);
        }
      } else if (this.markers.length === 0) {
        console.log('ℹ️ No markers to display, map remains at default position');
      }
      
      // Force one more resize after all markers are added
      if (this.map) {
        setTimeout(() => {
          if (this.map) {
            this.map.resize();
            console.log('✅ Final map resize after markers added');
          }
        }, 200);
      }
    },
    clearMarkers() {
      // Remove subscriber marker if it exists
      if (this.subscriberMarker) {
        try {
          this.subscriberMarker.remove();
        } catch (error) {
          // Skip if there's an error removing it
        }
        this.subscriberMarker = null;
      }
      this.subscriberCoords = null;
      
      // Validate markers exists and is an array before clearing
      if (this.markers && Array.isArray(this.markers)) {
        this.markers.forEach(marker => {
          try {
            if (marker && typeof marker.remove === 'function') {
              marker.remove();
            }
          } catch (error) {
            // Skip marker if there's an error removing it
          }
        });
        this.markers = [];
      } else {
        // Initialize if it doesn't exist
        this.markers = [];
      }
      
      // Validate popups exists and is an array before clearing
      if (this.popups && Array.isArray(this.popups)) {
        this.popups.forEach(popup => {
          try {
            if (popup && typeof popup.remove === 'function') {
              popup.remove();
            }
          } catch (error) {
            // Skip popup if there's an error removing it
          }
        });
        this.popups = [];
      } else {
        // Initialize if it doesn't exist
        this.popups = [];
      }
    },
    async addSubscriberMarker(bounds) {
      // Only add subscriber marker if we have member prop
      if (!this.member) {
        return;
      }
      
      // Build address string from member data
      const addressParts = [];
      if (this.member.ADDRESS || this.member.ADDRESS_1) {
        addressParts.push(this.member.ADDRESS || this.member.ADDRESS_1);
      }
      if (this.member.CITY) {
        addressParts.push(this.member.CITY);
      }
      if (this.member.STATE) {
        addressParts.push(this.member.STATE);
      }
      if (this.member.ZIP) {
        addressParts.push(this.member.ZIP);
      }
      
      if (addressParts.length === 0) {
        // No address info, skip
        return;
      }
      
      const addressString = addressParts.join(', ');

      // Check if member already has coordinates
      let lat = this.member.LAT || this.member.lat || this.member.LATITUDE || this.member.latitude;
      let lon = this.member.LON || this.member.lon || this.member.LONGITUDE || this.member.longitude;
      
      // Convert to numbers if they're strings
      if (lat && typeof lat === 'string') lat = parseFloat(lat);
      if (lon && typeof lon === 'string') lon = parseFloat(lon);
      
      // If no coordinates, geocode the address
      if ((!lat || !lon || isNaN(lat) || isNaN(lon)) && addressString) {
        try {

          const geocodeUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(addressString)}.json?access_token=${this.mapboxAccessToken}&limit=1`;
          
          const response = await fetch(geocodeUrl, {
            method: 'GET',
            credentials: 'omit'
          });
          
          const data = await response.json();
          
          if (data && data.features && data.features.length > 0) {
            const coordinates = data.features[0].center;
            lon = coordinates[0];
            lat = coordinates[1];

          } else {

            return;
          }
        } catch (error) {

          return;
        }
      }
      
      // Validate coordinates
      if (lat && lon && 
          !isNaN(lat) && !isNaN(lon) &&
          typeof lat === 'number' && typeof lon === 'number' &&
          lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        
        this.subscriberCoords = [lon, lat];
        
        // Create subscriber marker (different color - red/pink)
        const el = document.createElement('div');
        el.className = 'marker subscriber-marker';
        el.style.width = '35px';
        el.style.height = '35px';
        el.style.borderRadius = '50%';
        el.style.backgroundColor = '#e91e63'; // Pink/Red for subscriber
        el.style.border = '3px solid white';
        el.style.cursor = 'default';
        el.style.boxShadow = '0 3px 6px rgba(0,0,0,0.4)';
        el.style.position = 'relative';
        
        // Add icon inside (home icon)
        el.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="white" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>';
        
        try {
          this.subscriberMarker = new mapboxgl.Marker(el)
            .setLngLat(this.subscriberCoords)
            .addTo(this.map);

          // Extend bounds to include subscriber location
          if (bounds) {
            bounds.extend(this.subscriberCoords);
          }
        } catch (error) {

        }
      }
    },
    async selectProvider(provider) {
      if (!provider) {
        this.selectedProvider = null;
        this.hasSelectedProvider = false;
        return;
      }
      
      // Set flag immediately for button reactivity
      this.hasSelectedProvider = true;
      
      // Create a simplified copy to prevent deep reactivity issues
      let simplifiedProvider = null;
      try {
        simplifiedProvider = JSON.parse(JSON.stringify(provider));
      } catch (e) {
        simplifiedProvider = { ...provider };
      }
      
      // Set provider first to trigger reactivity
      this.selectedProvider = simplifiedProvider;
      
      // Fetch complete provider details from backend to get all fields
      if (provider && provider.PROVIDER_ID) {
        try {
          const detailParams = {};
          if (
            provider.PRACTICE_ID !== undefined &&
            provider.PRACTICE_ID !== null &&
            String(provider.PRACTICE_ID).trim() !== ''
          ) {
            detailParams.practiceId = provider.PRACTICE_ID;
          }
          const response = await axios.get(`/api/provider/${provider.PROVIDER_ID}`, { params: detailParams });
          if (response.data && response.data.length > 0) {
            // Merge complete provider data with existing data
            const completeProvider = response.data[0];
            // Create simplified copy of merged data
            try {
              this.selectedProvider = JSON.parse(JSON.stringify({
                ...provider,
                ...completeProvider
              }));
            } catch (e) {
              this.selectedProvider = {
                ...provider,
                ...completeProvider
              };
            }
          }
        } catch (error) {
          // Continue with existing provider data if fetch fails
        }
      }
      
      // Center map on provider if it has coordinates
      if (provider.SERVICE_LOCATION_LAT && provider.SERVICE_LOCATION_LON) {
        const lat = parseFloat(provider.SERVICE_LOCATION_LAT);
        const lon = parseFloat(provider.SERVICE_LOCATION_LON);
        
        if (!isNaN(lat) && !isNaN(lon)) {
          this.map.flyTo({
            center: [lon, lat],
            zoom: 14
          });
        }
      }
    },
    confirmSelection() {
      if (!this.hasSelectedProvider || !this.selectedProvider) {
        return;
      }
      
      // Ensure we emit the provider data correctly
      const providerToEmit = this.selectedProvider && typeof this.selectedProvider === 'object'
        ? { ...this.selectedProvider }
        : this.selectedProvider;
      
      // Validate that provider has required fields
      if (!providerToEmit || !providerToEmit.PROVIDER_ID) {
        return;
      }
      
      this.$emit('selected', providerToEmit);
    }
  }
};
</script>

<style scoped>
.marker {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #1976d2;
  border: 2px solid white;
  cursor: pointer;
}

/* OPTIMIZATION: Keep action buttons visible at bottom of modal */
.sticky-actions {
  position: sticky;
  bottom: 0;
  background-color: white;
  z-index: 10;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  padding: 12px 16px !important;
  margin-top: auto;
}

/* OPTIMIZATION: Adjust card text max-height for smaller screens */
@media (max-height: 800px) {
  .v-card-text {
    max-height: calc(100vh - 350px) !important;
  }
}

@media (max-height: 720px) {
  .v-card-text {
    max-height: calc(100vh - 400px) !important;
  }
  
  #mapId {
    height: 350px !important;
  }
}
</style>

